import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./ConstructionReport.css";
import Select from "react-select";
const RAW_API_BASE =
  process.env.REACT_APP_API_BASE_URL ? `${process.env.REACT_APP_API_BASE_URL}/api` : "http://localhost:8080/api";

// Remove trailing slash
const API_BASE = RAW_API_BASE.replace(/\/+$/, "");

// If URL already ends with /api then don't add it again
const API_PREFIX = API_BASE.endsWith("/api") ? API_BASE : `${API_BASE}/api`;
const FRONTEND_BASE =
  process.env.REACT_APP_FRONTEND_URL || window.location.origin;

const EXPORT_REPORT_CSS = `
  * { box-sizing: border-box; }

  :root {
    --ink: #172033;
    --muted: #667085;
    --line: #dbe3ee;
    --soft: #f6f8fc;
    --navy: #14234f;
    --navy-2: #1d3473;
    --accent: #2f6fed;
    --accent-soft: #edf3ff;
    --success: #087a55;
  }

  html,
  body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    color: var(--ink);
    font-family: Arial, Helvetica, sans-serif;
    -webkit-font-smoothing: antialiased;
  }

  .cr-export-document {
    width: 794px;
    margin: 0 auto;
    background: #ffffff;
  }

  /* ===================== PREMIUM HEADER ===================== */
  .cr-export-header {
    min-height: 116px;
    padding: 25px 34px 23px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 30px;
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, var(--navy) 0%, var(--navy-2) 100%);
    color: #ffffff;
  }

  .cr-export-header::after {
    content: "";
    position: absolute;
    right: -74px;
    top: -98px;
    width: 255px;
    height: 255px;
    border: 42px solid rgba(255, 255, 255, 0.055);
    border-radius: 50%;
  }

  .cr-export-header::before {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 5px;
    background: #4f8cff;
  }

  .cr-export-company,
  .cr-export-title {
    position: relative;
    z-index: 1;
  }

  .cr-export-company {
    display: flex;
    align-items: center;
    gap: 15px;
    min-width: 0;
  }

  .cr-export-logo {
    width: 62px;
    height: 62px;
    flex: 0 0 62px;
    display: grid;
    place-items: center;
    border-radius: 12px;
    background: #ffffff;
    color: var(--navy);
    font-size: 20px;
    font-weight: 900;
    letter-spacing: 0.08em;
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.18);
  }

  .cr-export-company h1,
  .cr-export-title h2,
  .cr-export-section-title h3,
  .cr-export-observation-title h2 {
    margin: 0;
  }

  .cr-export-company h1 {
    max-width: 320px;
    font-size: 19px;
    line-height: 1.22;
    letter-spacing: -0.01em;
  }

  .cr-export-company p,
  .cr-export-title p {
    margin: 6px 0 0;
    color: #cbd6ef;
    font-size: 9px;
    line-height: 1.4;
  }

  .cr-export-title {
    max-width: 285px;
    text-align: right;
  }

  .cr-export-title span {
    display: inline-block;
    padding: 5px 9px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    color: #eaf0ff;
    font-size: 7px;
    font-weight: 900;
    letter-spacing: 0.15em;
    text-transform: uppercase;
  }

  .cr-export-title h2 {
    margin-top: 9px;
    font-size: 22px;
    line-height: 1.14;
    letter-spacing: -0.02em;
  }

  /* ===================== PROJECT STRIP ===================== */
  .cr-export-strip {
    min-height: 72px;
    padding: 12px 34px;
    display: grid;
    grid-template-columns: 1.55fr 1fr;
    border-bottom: 1px solid var(--line);
    background: #f7f9fd;
  }

  .cr-export-strip > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
  }

  .cr-export-strip > div + div {
    padding-left: 26px;
    border-left: 1px solid var(--line);
  }

  .cr-export-label,
  .cr-export-text-box > span,
  .cr-export-photo-heading span,
  .cr-export-sign-box > span,
  .cr-export-authorized-sign > span {
    color: #6b7890;
    font-size: 7.5px;
    font-weight: 900;
    letter-spacing: 0.11em;
    text-transform: uppercase;
  }

  .cr-export-strip strong {
    color: var(--navy);
    font-size: 14px;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  /* ===================== SECTIONS ===================== */
  .cr-export-body,
  .cr-export-observation,
  .cr-export-signoff {
    padding: 22px 34px;
  }

  .cr-export-observation,
  .cr-export-signoff {
    border-top: 1px solid var(--line);
  }

  .cr-export-section-title {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 15px;
  }

  .cr-export-section-title > b {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    display: grid;
    place-items: center;
    border-radius: 9px;
    background: var(--navy);
    color: #ffffff;
    font-size: 9px;
    box-shadow: 0 5px 12px rgba(20, 35, 79, 0.16);
  }

  .cr-export-section-title h3 {
    color: var(--navy);
    font-size: 15px;
    line-height: 1.2;
  }

  .cr-export-section-title p {
    margin: 3px 0 0;
    color: var(--muted);
    font-size: 8px;
  }

  /* ===================== INFO CARDS ===================== */
  .cr-export-info-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 9px;
  }

  .cr-export-info {
    min-height: 64px;
    padding: 11px 12px;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: #ffffff;
  }

  .cr-export-location-info {
    grid-column: span 3;
    min-height: 70px;
    border-left: 4px solid var(--accent);
    background: #fbfcff;
  }

  .cr-export-info strong {
    display: block;
    margin-top: 6px;
    color: var(--ink);
    font-size: 9.5px;
    line-height: 1.45;
    overflow-wrap: anywhere;
  }

  .cr-export-info small {
    display: block;
    margin-top: 5px;
    color: #7a8699;
    font-size: 7.5px;
  }

  .cr-export-note {
    margin-top: 13px;
    padding: 11px 13px;
    border: 1px solid #dce6fb;
    border-radius: 8px;
    background: var(--accent-soft);
  }

  .cr-export-note strong {
    color: var(--navy);
    font-size: 8.5px;
  }

  .cr-export-note p {
    margin: 4px 0 0;
    color: #526078;
    font-size: 8px;
    line-height: 1.5;
  }

  /* ===================== OBSERVATION ===================== */
  .cr-export-observation {
    break-inside: auto;
    page-break-inside: auto;
  }

  .cr-export-observation-title {
    padding-bottom: 12px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    border-bottom: 2px solid #e9eef6;
  }

  .cr-export-observation-title > div > span {
    color: var(--accent);
    font-size: 7.5px;
    font-weight: 900;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .cr-export-observation-title h2 {
    margin-top: 5px;
    color: var(--navy);
    font-size: 19px;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  .cr-export-status {
    flex: 0 0 auto;
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 7px;
    font-weight: 900;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .status-open { background: #fff4e8; color: #b54708; }
  .status-in-progress { background: #eaf2ff; color: #2458c6; }
  .status-completed { background: #e8f8f1; color: #087a55; }
  .status-hold { background: #fff0f0; color: #b42318; }
  .status-not-applicable { background: #eef2f6; color: #596579; }

  .cr-export-meta {
    margin-top: 12px;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .cr-export-meta > div {
    min-height: 54px;
    padding: 9px 11px;
    border: 1px solid var(--line);
    border-radius: 7px;
    background: var(--soft);
  }

  .cr-export-meta strong {
    display: block;
    margin-top: 5px;
    color: var(--ink);
    font-size: 9px;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .cr-export-text-box {
    margin-top: 14px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .cr-export-text-box p,
  .cr-export-description-list {
    margin: 7px 0 0;
    padding: 11px 14px;
    border: 1px solid var(--line);
    border-left: 4px solid var(--accent);
    border-radius: 7px;
    background: #fbfcff;
    color: #354258;
    font-size: 9.5px;
    line-height: 1.58;
  }

  .cr-export-description-list {
    padding-left: 28px;
  }

  .cr-export-description-list li {
    margin: 0 0 5px;
    padding-left: 2px;
  }

  .cr-export-description-list li:last-child {
    margin-bottom: 0;
  }

  .cr-export-photo-heading {
    margin-top: 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .cr-export-photo-heading small {
    color: #7b8798;
    font-size: 7.5px;
  }

.cr-export-photo-grid {
  display: grid;
  grid-template-columns: repeat(4, 151px);
  gap: 10px;
  margin-top: 14px;
  margin-bottom: 24px;
  align-items: start;
  justify-content: start;
  break-inside: avoid;
  page-break-inside: avoid;
}

  .cr-export-photo-card {
    width: 151px;
    margin: 0;
    overflow: hidden;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: #f8fafc;
  }

  .cr-export-photo-card img {
    width: 151px;
    height: 151px;
    display: block;
    object-fit: cover;
    background: #e8edf4;
  }

  .cr-export-photo-card figcaption {
    padding: 6px 7px;
    border-top: 1px solid var(--line);
    color: #526078;
    font-size: 7.5px;
    font-weight: 700;
    text-align: center;
  }

  .cr-export-no-photo {
    margin-top: 8px;
    padding: 16px;
    border: 1px dashed #c8d2e1;
    border-radius: 8px;
    background: #fafbfd;
    color: #778398;
    font-size: 8px;
    text-align: center;
  }

  /* ===================== CONCLUSION ===================== */
  .cr-export-signoff {
    break-inside: auto;
    page-break-inside: auto;
  }

  .cr-export-summary {
    padding: 13px 14px;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: var(--soft);
  }

  .cr-export-summary p {
    margin: 7px 0 0;
    color: #354258;
    font-size: 9.5px;
    line-height: 1.55;
    white-space: pre-wrap;
  }

  .cr-export-authorized-sign {
    width: 46%;
    min-height: 106px;
    margin: 20px 0 0 auto;
    padding: 13px 14px 11px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: #ffffff;
  }

  .cr-export-authorized-space {
    width: 100%;
    height: 42px;
  }

  .cr-export-authorized-line {
    width: 100%;
    border-bottom: 1px solid #7d8796;
  }

  .cr-export-authorized-sign strong {
    margin-top: 8px;
    color: var(--navy);
    font-size: 9.5px;
  }

  .cr-export-authorized-sign small {
    margin-top: 3px;
    color: #788397;
    font-size: 7.5px;
  }

  .cr-export-sign-grid {
    margin-top: 18px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
  }

  .cr-export-sign-box {
    min-height: 110px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border: 1px solid var(--line);
    border-radius: 8px;
    text-align: center;
  }

  .cr-export-sign-box strong {
    margin-top: 8px;
    color: var(--ink);
    font-size: 9px;
  }

  .cr-export-sign-space { flex: 1; }
  .cr-export-sign-line { width: 88%; border-bottom: 1px solid #7d8796; }

  .cr-export-sign-box small {
    margin-top: 6px;
    color: #7a8699;
    font-size: 7.5px;
  }

  .cr-export-pdf-page-break {
    width: 100%;
    height: 0;
    display: block;
  }

  .cr-export-print-footer { display: none; }

  @media print {
    @page { size: A4 portrait; margin: 0; }

    html,
    body {
      width: 210mm;
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
    }

    .cr-export-document {
      width: 210mm;
      margin: 0;
      padding-bottom: 16mm;
    }

    .cr-export-header,
    .cr-export-strip,
    .cr-export-section-title > b,
    .cr-export-status,
    .cr-export-print-footer {
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }

    .cr-export-print-footer {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 9999;
      height: 15mm;
      padding: 3mm 9mm;
      display: flex !important;
      align-items: center;
      justify-content: space-between;
      background: var(--navy);
      color: #ffffff;
    }

    .cr-export-print-footer > div {
      display: flex;
      flex-direction: column;
      gap: 1mm;
    }

    .cr-export-print-footer > div:last-child { text-align: right; }
    .cr-export-print-footer strong { color: #ffffff; font-size: 8px; }
    .cr-export-print-footer span { color: #cbd6ef; font-size: 6.5px; }
  }
`;

const SUBJECT_OPTIONS = [
  "Architectural",
  "Civil",
  "Structural",
  "Electrical",
  "Plumbing",
  "HVAC",
  "Fire Fighting",
  "Interior",
  "Safety",
  "Quality",
  "Other",
];

const STATUS_OPTIONS = [
  "Open",
  "In Progress",
  "Completed",
  "Hold",
  "Not Applicable",
];

const defaultMarkerIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const makeObservation = () => ({
  clientId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  subject: "",
  description: "",
  remark: "",
  status: "Open",
  dueDate: "",
  assignedTo: "",
  photos: [],
});

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const readJson = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      data.message || `Request failed with status ${response.status}`,
    );
  }
  return data;
};

const getUserName = (user) =>
  user?.name || user?.fullName || user?.displayName || user?.email || "User";

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || "";
};

const toDateTimeLocal = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);

  return localDate.toISOString().slice(0, 16);
};

const toDateInput = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
};

const getSavedPhotoUrl = (photo) => {
  const rawUrl =
    photo?.url ||
    photo?.path ||
    photo?.filePath ||
    photo?.fileUrl ||
    photo?.imageUrl ||
    photo?.src ||
    "";

  const value = String(rawUrl || "").trim();
  if (!value) return "";

  if (
    value.startsWith("data:image/") ||
    value.startsWith("blob:") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return value;
  }

  const SERVER_BASE = String(API_BASE || "")
    .replace(/\/api$/, "")
    .replace(/\/+$/, "");

  const cleanPath = value.startsWith("/") ? value : `/${value}`;

  return `${SERVER_BASE}${cleanPath}`;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const readImageFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || "");

      if (!result.startsWith("data:image/")) {
        reject(new Error("Selected file is not a valid image."));
        return;
      }

      resolve(result);
    };

    reader.onerror = () =>
      reject(new Error(`Unable to read image: ${file?.name || "photo"}`));

    reader.readAsDataURL(file);
  });

const reverseGeocode = async (lat, lng) => {
  const url = new URL("https://nominatim.openstreetmap.org/reverse");
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("lat", lat);
  url.searchParams.set("lon", lng);
  url.searchParams.set("zoom", "18");
  url.searchParams.set("addressdetails", "1");

  const response = await fetch(url.toString(), {
    headers: { "Accept-Language": "en" },
  });

  if (!response.ok) throw new Error("Unable to load the selected address.");
  const result = await response.json();
  return result.display_name || `${lat}, ${lng}`;
};

function MapClickHandler({ onPick }) {
  useMapEvents({
    click(event) {
      onPick(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

function RecenterMap({ center }) {
  const map = useMap();

  useEffect(() => {
    if (center?.lat && center?.lng) {
      map.flyTo([center.lat, center.lng], 17, { duration: 0.7 });
    }
  }, [center, map]);

  return null;
}

function MapPicker({ open, value, onClose, onSelect }) {
  const markerRef = useRef(null);
  const [selected, setSelected] = useState(value || null);
  const [searchText, setSearchText] = useState(value?.address || "");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState("");

  const fallback = { lat: 23.0225, lng: 72.5714 };
  const center = selected?.lat && selected?.lng ? selected : fallback;

  useEffect(() => {
    if (!open) return;
    setSelected(value || null);
    setSearchText(value?.address || "");
    setSearchResults([]);
    setError("");
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const selectCoordinates = async (lat, lng, knownAddress = "") => {
    try {
      setError("");
      const address = knownAddress || (await reverseGeocode(lat, lng));
      setSelected({ address, lat: Number(lat), lng: Number(lng) });
      setSearchText(address);
      setSearchResults([]);
    } catch (err) {
      setSelected({
        address: `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`,
        lat: Number(lat),
        lng: Number(lng),
      });
      setError(err.message);
    }
  };

  const searchAddress = async () => {
    if (!searchText.trim()) return;

    try {
      setSearching(true);
      setError("");
      const url = new URL("https://nominatim.openstreetmap.org/search");
      url.searchParams.set("format", "jsonv2");
      url.searchParams.set("q", searchText.trim());
      url.searchParams.set("limit", "6");
      url.searchParams.set("addressdetails", "1");

      const response = await fetch(url.toString(), {
        headers: { "Accept-Language": "en" },
      });
      if (!response.ok) throw new Error("Unable to search for this address.");

      const results = await response.json();
      setSearchResults(results);
      if (!results.length) setError("No matching address was found.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Your browser does not support current location.");
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        await selectCoordinates(
          position.coords.latitude,
          position.coords.longitude,
        );
        setLocating(false);
      },
      () => {
        setError("Please allow location permission in your browser.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 12000 },
    );
  };

  const markerEvents = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (!marker) return;
        const position = marker.getLatLng();
        selectCoordinates(position.lat, position.lng);
      },
    }),
    [],
  );

  if (!open) return null;

  return createPortal(
    <div className="cr-modal-backdrop" onMouseDown={onClose}>
      <div className="cr-map-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="cr-modal-head">
          <div>
            <h3>Select Site Location</h3>
            <p>Search for an address, click on the map, or drag the marker.</p>
          </div>
          <button type="button" className="cr-modal-close" onClick={onClose}>
            Ã—
          </button>
        </div>

        <div className="cr-map-search-row">
          <div className="cr-search-wrap">
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  searchAddress();
                }
              }}
              placeholder="Search complete address / landmark"
            />
            {searchResults.length > 0 && (
              <div className="cr-search-results">
                {searchResults.map((result) => (
                  <button
                    type="button"
                    key={result.place_id}
                    onClick={() =>
                      selectCoordinates(
                        Number(result.lat),
                        Number(result.lon),
                        result.display_name,
                      )
                    }
                  >
                    {result.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            className="cr-btn cr-btn-secondary"
            disabled={searching}
            onClick={searchAddress}
          >
            {searching ? "Searching..." : "Search"}
          </button>

          <button
            type="button"
            className="cr-btn cr-btn-light"
            disabled={locating}
            onClick={useCurrentLocation}
          >
            {locating ? "Locating..." : "My Location"}
          </button>
        </div>

        {error && <div className="cr-map-error">{error}</div>}

        <MapContainer
          center={[center.lat, center.lng]}
          zoom={selected?.lat ? 17 : 11}
          className="cr-map-canvas"
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onPick={selectCoordinates} />
          <RecenterMap center={selected} />
          {selected?.lat && selected?.lng && (
            <Marker
              ref={markerRef}
              position={[selected.lat, selected.lng]}
              icon={defaultMarkerIcon}
              draggable
              eventHandlers={markerEvents}
            />
          )}
        </MapContainer>

        <div className="cr-selected-address">
          <strong>Selected Address</strong>
          <span>{selected?.address || "Select a location on the map"}</span>
          {selected?.lat && (
            <small>
              {selected.lat.toFixed(6)}, {selected.lng.toFixed(6)}
            </small>
          )}
        </div>

        <div className="cr-modal-actions">
          <button
            type="button"
            className="cr-btn cr-btn-light"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cr-btn cr-btn-primary"
            disabled={!selected?.address}
            onClick={() => onSelect(selected)}
          >
            Use This Location
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function ConstructionReport({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [previewImage, setPreviewImage] = useState(null);
  const reportId = searchParams.get("reportId");

  const [loadingReport, setLoadingReport] = useState(false);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [availableEmails, setAvailableEmails] = useState([]);
  const [mapOpen, setMapOpen] = useState(false);
  const [savedReport, setSavedReport] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const [form, setForm] = useState({
    project: "",
    projectName: "",
    visitDateTime: new Date().toISOString().slice(0, 16),
    siteLocation: null,
    members: [],
    contractor: "",
    clientRepresentative: "",
    preparedBy: user?._id || "",
    reviewedBy: "",
    overallRemark: "",
  });

  const [observations, setObservations] = useState([makeObservation()]);

  useEffect(() => {
    const loadLists = async () => {
      try {
        setLoadingLists(true);
        const [projectResponse, userResponse] = await Promise.all([
          fetch(`${API_BASE}/projects`, { headers: authHeaders() }),
          fetch(`${API_BASE}/users`, { headers: authHeaders() }),
        ]);

        const projectData = await readJson(projectResponse);
        const userData = await readJson(userResponse);

        setProjects(
          Array.isArray(projectData) ? projectData : projectData.projects || [],
        );
        setUsers(Array.isArray(userData) ? userData : userData.users || []);
      } catch (error) {
        alert(error.message);
      } finally {
        setLoadingLists(false);
      }
    };

    loadLists();
  }, []);

  useEffect(() => {
    const loadSavedReport = async () => {
      if (!reportId) return;

      try {
        setLoadingReport(true);

        const response = await fetch(
          `${API_PREFIX}/construction-reports/${reportId}`,
          {
            headers: authHeaders(),
          },
        );

        const data = await readJson(response);
        const report = data.report || data;

        setForm({
          project: getId(report.project),
          projectName:
            report.projectName ||
            report.project?.projectName ||
            report.project?.name ||
            "",
          visitDateTime: toDateTimeLocal(report.visitDateTime),
          siteLocation: report.siteLocation
            ? {
                address: report.siteLocation.address || "",
                lat: Number(report.siteLocation.lat),
                lng: Number(report.siteLocation.lng),
                placeId: report.siteLocation.placeId || "",
              }
            : null,
          members: Array.isArray(report.members)
            ? report.members.map((member) => getId(member)).filter(Boolean)
            : [],
          contractor: report.contractor || "",
          clientRepresentative: report.clientRepresentative || "",
          preparedBy: getId(report.preparedBy),
          reviewedBy: report.reviewedBy || "",
          overallRemark: report.overallRemark || "",
        });

        setObservations(
          Array.isArray(report.observations) && report.observations.length
            ? report.observations.map((observation, index) => ({
                clientId: observation._id || `saved-${index}-${Date.now()}`,
                subject: observation.subject || "",
                description: observation.description || "",
                remark: observation.remark || "",
                status: observation.status || "Open",
                dueDate: toDateInput(observation.dueDate),
                assignedTo: getId(observation.assignedTo),
                photos: Array.isArray(observation.photos)
                  ? observation.photos.map((photo, photoIndex) => ({
                      file: null,
                      preview: getSavedPhotoUrl(photo),
                      name:
                        photo.fileName || `Observation photo ${photoIndex + 1}`,
                      existing: true,
                      url: photo.url || "",
                      path: photo.path || "",
                      filePath: photo.filePath || "",
                      fileUrl: photo.fileUrl || "",
                      imageUrl: photo.imageUrl || "",
                      src: photo.src || "",
                      fileName:
                        photo.fileName || photo.filename || photo.name || "",
                      mimeType: photo.mimeType || photo.type || "",
                      dataUrl: "",
                    }))
                  : [],
              }))
            : [makeObservation()],
        );

        setSavedReport(report);
      } catch (error) {
        console.error("Unable to load saved report:", error);
        alert(error.message);
      } finally {
        setLoadingReport(false);
      }
    };

    loadSavedReport();
  }, [reportId]);

  const completedCount = useMemo(
    () => observations.filter((o) => o.status === "Completed").length,
    [observations],
  );

  const updateForm = (key, value) =>
    setForm((old) => ({ ...old, [key]: value }));

  const updateObservation = (id, key, value) =>
    setObservations((old) =>
      old.map((o) => (o.clientId === id ? { ...o, [key]: value } : o)),
    );

  // Adds a new bullet point from the + Add Point button.
  const addDescriptionPoint = (observationId) => {
    setObservations((old) =>
      old.map((observation) => {
        if (observation.clientId !== observationId) return observation;

        const currentDescription = String(
          observation.description || "",
        ).trimEnd();
        const nextDescription = currentDescription
          ? `${currentDescription}\nâ€¢ `
          : "â€¢ ";

        return {
          ...observation,
          description: nextDescription,
        };
      }),
    );

    setSavedReport(null);
  };

  // Converts saved bullet text into a clean array for PDF/print export.
  const getDescriptionPoints = (value) =>
    String(value || "")
      .split(/\r?\n/)
      .map((line) => line.replace(/^\s*[â€¢*-]\s*/, "").trim())
      .filter(Boolean);

  // Selects a project and stores both its ID and display name.
  const onProjectChange = (projectId) => {
    const selected = projects.find((p) => p._id === projectId);
    setForm((old) => ({
      ...old,
      project: projectId,
      projectName: selected?.projectName || selected?.name || "",
    }));
    setSavedReport(null);
  };

  // Validates, previews and attaches up to 4 images to one observation.
  const addPhotos = async (observationId, fileList) => {
    const selectedFiles = Array.from(fileList || []).filter(
      (file) => file instanceof File && file.type?.startsWith("image/"),
    );

    const currentObservation = observations.find(
      (item) => item.clientId === observationId,
    );

    const availableSlots = Math.max(
      0,
      4 - (currentObservation?.photos?.length || 0),
    );

    const filesToAdd = selectedFiles.slice(0, availableSlots);

    if (!filesToAdd.length) return;

    try {
      const preparedPhotos = await Promise.all(
        filesToAdd.map(async (file) => {
          const dataUrl = await readImageFileAsDataUrl(file);

          return {
            file,
            preview: dataUrl,
            dataUrl,
            name: file.name,
            fileName: file.name,
            mimeType: file.type,
            existing: false,
          };
        }),
      );

      setObservations((old) =>
        old.map((observation) =>
          observation.clientId === observationId
            ? {
                ...observation,
                photos: [...observation.photos, ...preparedPhotos].slice(0, 4),
              }
            : observation,
        ),
      );

      console.log(
        "[construction-report] SELECTED PHOTOS:",
        preparedPhotos.map((photo) => ({
          name: photo.name,
          type: photo.mimeType,
          size: photo.file?.size,
          isFile: photo.file instanceof File,
          hasDataUrl: photo.dataUrl?.startsWith("data:image/"),
        })),
      );

      setSavedReport(null);
    } catch (error) {
      console.error("Unable to add photos:", error);
      alert(error.message || "Unable to add the selected photos.");
    }
  };

  // Removes one photo from an observation.
  const removePhoto = (observationId, index) => {
    setObservations((old) =>
      old.map((o) => {
        if (o.clientId !== observationId) return o;
        const photo = o.photos[index];
        if (photo?.preview && !photo.existing)
          URL.revokeObjectURL(photo.preview);
        return { ...o, photos: o.photos.filter((_, i) => i !== index) };
      }),
    );
    setSavedReport(null);
  };

  // Removes an observation while keeping at least one card on screen.
  const removeObservation = (clientId) => {
    setObservations((old) => {
      if (old.length === 1) return old;
      const target = old.find((o) => o.clientId === clientId);
      target?.photos?.forEach((photo) => {
        if (photo.preview && !photo.existing)
          URL.revokeObjectURL(photo.preview);
      });
      return old.filter((o) => o.clientId !== clientId);
    });
    setSavedReport(null);
  };

  // Checks required report fields before save, print or PDF export.
  const validate = () => {
    if (!form.project) return (alert("Please select a project."), false);
    if (!form.visitDateTime)
      return (alert("Please select the visit date and time."), false);
    if (!form.siteLocation?.address)
      return (alert("Please select the site location."), false);

    if (!form.preparedBy) return (alert("Please select Prepared By."), false);
    if (!observations.some((o) => o.description.trim()))
      return (alert("Please add at least one observation."), false);
    return true;
  };

  // Builds multipart FormData: JSON report payload + real image File objects.
  const buildFormData = () => {
    const body = new FormData();

    const cleanObservations = observations.map((observation) => {
      const uploadPhotos = observation.photos.filter(
        (photo) => photo?.file instanceof File,
      );

      return {
        clientId: observation.clientId,
        subject: observation.subject,
        description: observation.description,
        remark: observation.remark,
        status: observation.status,
        dueDate: observation.dueDate || null,
        assignedTo: observation.assignedTo || null,
        photoCount: uploadPhotos.length,
      };
    });

    body.append(
      "report",
      JSON.stringify({
        ...form,
        observations: cleanObservations,
      }),
    );

    let appendedPhotoCount = 0;

    observations.forEach((observation, observationIndex) => {
      observation.photos.forEach((photo) => {
        if (photo?.file instanceof File) {
          body.append(
            "observationPhotos",
            photo.file,
            photo.file.name || `observation-${observationIndex + 1}.jpg`,
          );

          appendedPhotoCount += 1;
        }
      });
    });

    console.log("[construction-report] UPLOAD SUMMARY:", {
      appendedPhotoCount,
      observationPhotoCounts: cleanObservations.map((observation, index) => ({
        observation: index + 1,
        photoCount: observation.photoCount,
      })),
    });

    for (const [key, value] of body.entries()) {
      console.log(
        "[FORM DATA]",
        key,
        value instanceof File
          ? {
              name: value.name,
              type: value.type,
              size: value.size,
            }
          : value,
      );
    }

    return body;
  };

  // Saves the construction report to the backend.
  const saveReport = async () => {
    if (!validate()) return null;

    try {
      setSaving(true);
      const response = await fetch(`${API_BASE}/construction-reports`, {
        method: "POST",
        headers: authHeaders(),
        body: buildFormData(),
      });

      const data = await readJson(response);
      const report = data.report || data;
      setSavedReport(report);
      alert("Construction report successfully saved.");
      return report;
    } catch (error) {
      alert(error.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Email and WhatsApp require a saved report ID; save first when needed.
  const ensureSaved = async () => savedReport || (await saveReport());

  // Opens the recipient picker modal (multi-select dropdown of saved client
  // emails), instead of a single window.prompt.
  const sendEmail = async () => {
    const report = await ensureSaved();
    if (!report?._id) return;

    const selectedProject = projects.find(
      (project) => String(project._id) === String(form.project),
    );

    if (!selectedProject) {
      alert("Please select a project.");
      return;
    }

    const emails = Array.from(
      new Set(
        [...(selectedProject.clientEmails || []), selectedProject.clientEmail]
          .filter(Boolean)
          .map((email) => email.trim().toLowerCase()),
      ),
    );

    setAvailableEmails(emails);
    setSelectedEmails(emails.length ? [emails[0]] : []);
    setNewEmail("");
    setEmailModalOpen(true);
  };

  // Validates and adds a custom email typed into the recipient modal to the
  // dropdown list (does not save it to the project yet â€” that happens on
  // Send, only for emails actually used).
  const addNewEmailToList = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isValidEmail) {
      alert("Please enter a valid email address.");
      return;
    }

    setAvailableEmails((old) => (old.includes(email) ? old : [...old, email]));
    setSelectedEmails((old) => (old.includes(email) ? old : [...old, email]));
    setNewEmail("");
  };

  // Generates the PDF and sends it to every selected recipient in one go
  // (comma-separated "to" field â€” nodemailer-style mailers accept a
  // comma-separated list of recipients for a single send).
  const confirmSendEmail = async () => {
    if (!savedReport?._id) {
      alert("Please save the report before sending an email.");
      return;
    }

    const recipients = [...selectedEmails];
    const typedEmail = newEmail.trim().toLowerCase();

    if (typedEmail) {
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(typedEmail);
      if (!isValidEmail) {
        alert("Please enter a valid email address.");
        return;
      }
      if (!recipients.includes(typedEmail)) recipients.push(typedEmail);
    }

    if (!recipients.length) {
      alert("Please select or type at least one recipient email.");
      return;
    }

    console.log("[construction-report] Sending email to:", recipients);

    try {
      setSendingEmail(true);

      const pdfBlob = await downloadPdf();

      if (!pdfBlob) {
        alert("Unable to generate PDF.");
        return;
      }

      // Save any brand-new client emails on the project so they appear in
      // the dropdown next time.
      const newEmailsToSave = recipients.filter(
        (email) => !availableEmails.includes(email),
      );

      await Promise.all(
        newEmailsToSave.map((email) =>
          fetch(`${API_BASE}/api/projects/${form.project}/add-client-email`, {
            method: "POST",
            headers: {
              ...authHeaders(),
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email }),
          }).catch((error) => {
            console.error(
              "[construction-report] Unable to save new client email:",
              email,
              error,
            );
          }),
        ),
      );

      const formData = new FormData();
      formData.append("pdf", pdfBlob, "Construction_Report.pdf");
      formData.append("to", recipients.join(","));

      const endpoint = `${API_BASE}/construction-reports/${savedReport._id}/send-email`;

      console.log("[construction-report] POST", endpoint, {
        recipients,
      });

      const response = await fetch(endpoint, {
        method: "POST",
        headers: authHeaders(),
        body: formData,
      });

      console.log(
        "[construction-report] Email response status:",
        response.status,
      );

      await readJson(response);

      alert(`Email sent successfully to ${recipients.length} recipient(s).`);

      setAvailableEmails((old) => Array.from(new Set([...old, ...recipients])));
      setSelectedEmails(recipients);
      setNewEmail("");
      setEmailModalOpen(false);
    } catch (error) {
      console.error("Send email failed:", error);
      alert(
        error.message ||
          "Unable to send the report email. Check the browser console/network tab for details.",
      );
    } finally {
      setSendingEmail(false);
    }
  };

  // Opens WhatsApp with project, report and map links pre-filled â€” and
  // attaches the actual PDF via the Web Share API where supported.
  const sendWhatsApp = async () => {
    if (!validate()) return;

    const report = await ensureSaved();
    if (!report?._id) return;

    const phone = window.prompt(
      "Enter the WhatsApp number with country code. Leave blank to choose a contact:",
      "91",
    );

    if (phone === null) return;

    const publicUrl = report.publicToken
      ? `${FRONTEND_BASE}/construction-report/public/${report.publicToken}`
      : `${FRONTEND_BASE}/construction-reports/${report._id}`;

    const mapUrl = `https://www.openstreetmap.org/?mlat=${form.siteLocation.lat}&mlon=${form.siteLocation.lng}#map=18/${form.siteLocation.lat}/${form.siteLocation.lng}`;

    const message = [
      "Construction Site Visit Report",
      `Project: ${form.projectName}`,
      `Visit: ${new Date(form.visitDateTime).toLocaleString()}`,
      `Location: ${form.siteLocation.address}`,
      `Observations: ${observations.length}`,
      `Report: ${publicUrl}`,
      `Map: ${mapUrl}`,
    ].join("\n");

    // ---- Try to attach the actual PDF via the Web Share API ----
    try {
      const pdfBlob = await downloadPdf();

      if (pdfBlob) {
        const safeName = String(form.projectName || "Construction_Report")
          .replace(/[^a-zA-Z0-9-_ ]/g, "")
          .trim()
          .replace(/\s+/g, "_");

        const pdfFile = new File([pdfBlob], `${safeName}_Report.pdf`, {
          type: "application/pdf",
        });

        const canShareFiles =
          navigator.canShare && navigator.canShare({ files: [pdfFile] });

        if (canShareFiles) {
          await navigator.share({
            title: "Construction Site Visit Report",
            text: message,
            files: [pdfFile],
          });
          return; // user picked WhatsApp (or another app) from the share sheet
        }
      }
    } catch (error) {
      // AbortError just means the user cancelled the share sheet â€” not a failure.
      if (error?.name !== "AbortError") {
        console.warn(
          "[construction-report] Web Share PDF attach failed, falling back to link:",
          error,
        );
      } else {
        return;
      }
    }

    // ---- Fallback: text-only wa.me link (no file, just the report link) ----
    const cleanPhone = phone.replace(/\D/g, "");
    const whatsappUrl = cleanPhone
      ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  // ---------- Formal report export for PRINT and PDF ----------

  const getReportUserName = (userId) => {
    const matchedUser = users.find((item) => item._id === userId);
    return matchedUser ? getUserName(matchedUser) : "-";
  };

  const formatReportDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatReportDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /* =====================================================
     IMAGE EXPORT HELPERS
     Every image is converted to a data URL before Print/PDF.
  ===================================================== */
  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = String(reader.result || "");

        if (!result.startsWith("data:image/")) {
          reject(new Error("The selected file is not a valid image."));
          return;
        }

        resolve(result);
      };

      reader.onerror = () =>
        reject(new Error("Unable to convert the image for export."));

      reader.readAsDataURL(blob);
    });

  const waitForImages = async (node) => {
    if (!node) return;

    const images = Array.from(node.querySelectorAll("img"));

    await Promise.all(
      images.map(
        (image) =>
          new Promise((resolve) => {
            let settled = false;

            const finish = () => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeoutId);
              image.removeEventListener("load", onLoad);
              image.removeEventListener("error", onError);
              resolve();
            };

            const onLoad = () => finish();
            const onError = () => {
              console.warn(
                "[construction-report] Export image failed:",
                image.src?.slice(0, 180),
              );
              finish();
            };

            const timeoutId = window.setTimeout(onError, 20000);

            if (image.complete && image.naturalWidth > 0) {
              finish();
              return;
            }

            image.addEventListener("load", onLoad, { once: true });
            image.addEventListener("error", onError, { once: true });
          }),
      ),
    );

    await new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve)),
    );
  };

  const getAbsolutePhotoUrl = (value) => {
    const rawValue = String(value || "").trim();
    if (!rawValue) return "";

    if (
      rawValue.startsWith("data:image/") ||
      rawValue.startsWith("blob:") ||
      rawValue.startsWith("http://") ||
      rawValue.startsWith("https://")
    ) {
      return rawValue;
    }
    const SERVER_BASE = String(API_BASE || "")
      .replace(/\/api$/, "")
      .replace(/\/+$/, "");

    const cleanPath = rawValue.startsWith("/") ? rawValue : `/${rawValue}`;

    return `${SERVER_BASE}${cleanPath}`;
  };

  // Triggers an actual browser file download for a blob.
  const triggerBlobDownload = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const getPhotoSource = (photo) =>
    photo?.dataUrl ||
    photo?.exportSrc ||
    photo?.preview || // <-- preview ko pehle use karo
    photo?.url ||
    photo?.path ||
    photo?.filePath ||
    photo?.fileUrl ||
    photo?.imageUrl ||
    photo?.src ||
    "";

  const inferImageMimeType = (url, currentType = "") => {
    if (String(currentType).startsWith("image/")) return currentType;
    if (/\.png(?:\?|$)/i.test(url)) return "image/png";
    if (/\.webp(?:\?|$)/i.test(url)) return "image/webp";
    if (/\.gif(?:\?|$)/i.test(url)) return "image/gif";
    if (/\.svg(?:\?|$)/i.test(url)) return "image/svg+xml";
    return "image/jpeg";
  };

  const fetchImageAsDataUrl = async (rawUrl) => {
    const url = getAbsolutePhotoUrl(rawUrl);
    if (!url) return "";
    if (url.startsWith("data:image/")) return url;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-store",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error(`Image request failed with status ${response.status}`);
      }

      const originalBlob = await response.blob();
      const responseType = response.headers.get("content-type") || "";
      const mimeType = inferImageMimeType(
        url,
        originalBlob.type || responseType,
      );

      const imageBlob = originalBlob.type?.startsWith("image/")
        ? originalBlob
        : new Blob([originalBlob], { type: mimeType });

      return await blobToDataUrl(imageBlob);
    } catch (error) {
      console.error("[construction-report] Image export failed:", {
        url,
        error,
      });

      /*
        Static upload URL fallback. Print window can use it directly and
        html2canvas can use it when the backend sends CORS headers.
      */
      return url;
    }
  };

  const resolvePhotoExportSrc = async (photo) => {
    const directDataUrl = [
      photo?.dataUrl,
      photo?.preview,
      photo?.exportSrc,
    ].find((value) => String(value || "").startsWith("data:image/"));

    if (directDataUrl) return directDataUrl;

    if (photo?.file instanceof File || photo?.file instanceof Blob) {
      try {
        return await blobToDataUrl(photo.file);
      } catch (error) {
        console.error("Unable to convert local image:", error);
      }
    }

    return await fetchImageAsDataUrl(getPhotoSource(photo));
  };

  const prepareExportObservations = async () => {
    const result = await Promise.all(
      observations.map(async (observation, observationIndex) => {
        const sourcePhotos = Array.isArray(observation.photos)
          ? observation.photos.slice(0, 4)
          : [];

        const preparedPhotos = await Promise.all(
          sourcePhotos.map(async (photo, photoIndex) => {
            const exportSrc = await resolvePhotoExportSrc(photo);

            console.log("[construction-report] Export photo result:", {
              observation: observationIndex + 1,
              photo: photoIndex + 1,
              source: getPhotoSource(photo),
              exportSrcReady: Boolean(exportSrc),
              isDataUrl: String(exportSrc || "").startsWith("data:image/"),
            });

            return { ...photo, exportSrc };
          }),
        );

        return {
          ...observation,
          photos: preparedPhotos.filter((photo) => Boolean(photo.exportSrc)),
        };
      }),
    );

    const sourceCount = observations.reduce(
      (total, observation) => total + (observation.photos?.length || 0),
      0,
    );
    const exportCount = result.reduce(
      (total, observation) => total + (observation.photos?.length || 0),
      0,
    );

    if (sourceCount > 0 && exportCount === 0) {
      throw new Error(
        "Attached photos could not be loaded for PDF/Print. Please verify the backend upload URL.",
      );
    }

    return result;
  };

  /*
    html2canvas creates one long canvas. This function inserts blank space
    before large sections so a footer never cuts through a heading/card.
  */
  const applyPdfPageBreaks = (reportDocument) => {
    if (!reportDocument) return;

    reportDocument
      .querySelectorAll(".cr-export-pdf-page-break")
      .forEach((element) => element.remove());

    /*
    PDF layout:
    A4 height = 297 mm
    Footer = 16 mm
    Next-page top margin = 8 mm
  */
    const footerHeightMm = 16;
    const nextPageTopMarginMm = 8;

    const pageHeightPx = Math.round((794 * 297) / 210);

    const footerHeightPx = Math.round((794 * footerHeightMm) / 210);

    const topMarginPx = Math.round((794 * nextPageTopMarginMm) / 210);

    const usablePageHeight = pageHeightPx - footerHeightPx;

    /*
    Elements that should never be cut in half.
  */
    const protectedSelectors = [
      ".cr-export-photo-grid",
      ".cr-export-authorized-sign",
    ];

    protectedSelectors.forEach((selector) => {
      const elements = Array.from(reportDocument.querySelectorAll(selector));

      elements.forEach((element) => {
        const elementTop = element.offsetTop;
        const elementHeight = element.offsetHeight;

        /*
        Current page position.
      */
        const topOnPage = elementTop % usablePageHeight;

        const remainingSpace = usablePageHeight - topOnPage;

        /*
        Element current page me poora fit nahi ho raha.
        Aur element ek complete page se chhota hai.
      */
        const shouldMoveToNextPage =
          elementHeight <= usablePageHeight - topMarginPx &&
          elementHeight > remainingSpace;

        if (!shouldMoveToNextPage) return;

        const spacer = reportDocument.ownerDocument.createElement("div");

        spacer.className = "cr-export-pdf-page-break";

        /*
        Current page ka remaining area blank chhodega,
        phir next page ke top margin ke baad photo grid aayegi.
      */
        spacer.style.height = `${remainingSpace + topMarginPx}px`;

        spacer.style.width = "100%";
        spacer.style.display = "block";

        element.parentNode.insertBefore(spacer, element);
      });
    });
  };

  // Generates the formal HTML used by both browser print and PDF export.
  const buildReportExportHtml = (exportObservations = observations) => {
    const memberNames = form.members
      .map((memberId) => getReportUserName(memberId))
      .filter((name) => name && name !== "-")
      .join(", ");

    const observationHtml = exportObservations
      .map((observation, observationIndex) => {
        const statusClass = String(observation.status || "Open")
          .toLowerCase()
          .replace(/\s+/g, "-");

        const photoHtml = observation.photos.length
          ? `
            <div class="cr-export-photo-grid">
              ${observation.photos
                .slice(0, 4)
                .map(
                  (photo, photoIndex) => `
                    <figure class="cr-export-photo-card">
                      <img
                        src="${escapeHtml(photo.exportSrc || getAbsolutePhotoUrl(getPhotoSource(photo)) || "")}"
                        alt="Observation ${observationIndex + 1} photo ${
                          photoIndex + 1
                        }"
                        crossorigin="anonymous"
                        decoding="sync"
                      />
                      <figcaption>Photo ${photoIndex + 1}</figcaption>
                    </figure>
                  `,
                )
                .join("")}
            </div>
          `
          : `
            <div class="cr-export-no-photo">
              No photographic evidence attached to this observation.
            </div>
          `;

        return `
          <section class="cr-export-observation">
            <div class="cr-export-observation-title">
              <div>
                <span>Observation ${String(observationIndex + 1).padStart(
                  2,
                  "0",
                )}</span>
                <h2>${escapeHtml(
                  observation.subject || "General Site Observation",
                )}</h2>
              </div>

              <div class="cr-export-status status-${statusClass}">
                ${escapeHtml(observation.status || "Open")}
              </div>
            </div>

            <div class="cr-export-meta">
              <div>
                <span class="cr-export-label">Assigned To</span>
                <strong>${escapeHtml(
                  getReportUserName(observation.assignedTo),
                )}</strong>
              </div>

              <div>
                <span class="cr-export-label">Target Date</span>
                <strong>${escapeHtml(
                  formatReportDate(observation.dueDate),
                )}</strong>
              </div>

              <div>
                <span class="cr-export-label">Visit Date</span>
                <strong>${escapeHtml(
                  formatReportDate(form.visitDateTime),
                )}</strong>
              </div>
            </div>

            <div class="cr-export-text-box">
              <span>Description / Site Observation With Solution</span>
              ${
                getDescriptionPoints(observation.description).length
                  ? `<ul class="cr-export-description-list">
                      ${getDescriptionPoints(observation.description)
                        .map((point) => `<li>${escapeHtml(point)}</li>`)
                        .join("")}
                    </ul>`
                  : `<p>-</p>`
              }
            </div>

            <div class="cr-export-photo-heading">
              <span>Photographic Evidence</span>
              <small>${observation.photos.filter((photo) => Boolean(photo.exportSrc || photo.preview)).length} photo(s)</small>
            </div>

            ${photoHtml}
          </section>
        `;
      })
      .join("");

    return `
      <div class="cr-export-document">
        <header class="cr-export-header">
          <div class="cr-export-company">
            <div class="cr-export-logo">SDC</div>

            <div>
              <h1>Sheth Design Consultants LLP</h1>
              <p>Architecture | Engineering | Project Management</p>
            </div>
          </div>

          <div class="cr-export-title">
            <span>Official Site Report</span>
            <h2>Construction Site Visit Report</h2>
            <p>Generated on ${escapeHtml(formatReportDateTime(new Date()))}</p>
          </div>
        </header>

        <div class="cr-export-strip">
          <div>
            <span class="cr-export-label">Project</span>
            <strong>${escapeHtml(form.projectName || "-")}</strong>
          </div>

          <div>
            <span class="cr-export-label">Visit Date & Time</span>
            <strong>${escapeHtml(
              formatReportDateTime(form.visitDateTime),
            )}</strong>
          </div>
        </div>

        <section class="cr-export-body">
          <div class="cr-export-section-title">
            <b>01</b>

            <div>
              <h3>Project & Visit Information</h3>
              <p>General site inspection information</p>
            </div>
          </div>

          <div class="cr-export-info-grid">
            <div class="cr-export-info cr-export-location-info">
              <span class="cr-export-label">Site Location</span>
              <strong>${escapeHtml(form.siteLocation?.address || "-")}</strong>
              ${
                form.siteLocation?.lat && form.siteLocation?.lng
                  ? `<small>Coordinates: ${Number(
                      form.siteLocation.lat,
                    ).toFixed(
                      6,
                    )}, ${Number(form.siteLocation.lng).toFixed(6)}</small>`
                  : ""
              }
            </div>

            <div class="cr-export-info">
              <span class="cr-export-label">Prepared By</span>
              <strong>${escapeHtml(getReportUserName(form.preparedBy))}</strong>
            </div>

            <div class="cr-export-info">
              <span class="cr-export-label">Received By</span>
              <strong>${escapeHtml(form.reviewedBy || "-")}</strong>
            </div>

            <div class="cr-export-info">
              <span class="cr-export-label">Site Member</span>
              <strong>${escapeHtml(memberNames || "-")}</strong>
            </div>

            <div class="cr-export-info">
              <span class="cr-export-label">Contractor</span>
              <strong>${escapeHtml(form.contractor || "-")}</strong>
            </div>

            <div class="cr-export-info">
              <span class="cr-export-label">Client Representative</span>
              <strong>${escapeHtml(form.clientRepresentative || "-")}</strong>
            </div>

            <div class="cr-export-info">
              <span class="cr-export-label">Total Observations</span>
              <strong>${exportObservations.length}</strong>
            </div>
          </div>

          <div class="cr-export-note">
            <strong>Report Note</strong>
            <p>
              Detailed observations, recommendations and photographic evidence
              follow immediately below.
            </p>
          </div>
        </section>

        ${observationHtml}
        <section class="cr-export-signoff">
          <div class="cr-export-section-title">
            <b>03</b>

            <div>
              <h3>Conclusion & Sign-off</h3>
              <p>Final site visit remarks and authorization</p>
            </div>
          </div>

          <div class="cr-export-summary">
            <span class="cr-export-label">Overall Site Visit Remark</span>
            <p>${escapeHtml(form.overallRemark || "-").replace(
              /\n/g,
              "<br/>",
            )}</p>
          </div>

          <div class="cr-export-authorized-sign">
            <span class="cr-export-label">Authorized Signature</span>
            <div class="cr-export-authorized-space"></div>
            <div class="cr-export-authorized-line"></div>
            <strong>Authorized Signatory</strong>
            <small>Signature & Company Seal</small>
          </div>
        </section>

        <footer class="cr-export-print-footer">
          <div>
            <strong>Sheth Design Consultants LLP</strong>
            <span>Construction Site Visit Report</span>
          </div>

          <div>
            <span>${escapeHtml(form.projectName || "-")}</span>
            <span>${escapeHtml(
              formatReportDate(savedReport?.createdAt || new Date()),
            )}</span>
          </div>
        </footer>
      </div>
    `;
  };

  // Opens a dedicated print window containing only the formal report.
  const printReport = async () => {
    if (!validate()) return;

    const printWindow = window.open("", "_blank", "width=1000,height=800");

    if (!printWindow) {
      alert("Please allow pop-ups to print the report.");
      return;
    }

    try {
      const exportObservations = await prepareExportObservations();

      printWindow.document.open();
      printWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta
              name="viewport"
              content="width=device-width,initial-scale=1"
            />
            <title>Construction Site Visit Report</title>
            <style>${EXPORT_REPORT_CSS}</style>
          </head>

          <body>
            ${buildReportExportHtml(exportObservations)}
          </body>
        </html>
      `);
      printWindow.document.close();

      await new Promise((resolve) => {
        if (printWindow.document.readyState === "complete") {
          resolve();
          return;
        }

        printWindow.addEventListener("load", resolve, { once: true });
      });

      await waitForImages(printWindow.document.body);
      await new Promise((resolve) => window.setTimeout(resolve, 250));

      printWindow.focus();
      printWindow.print();
    } catch (error) {
      console.error("Print report failed:", error);
      printWindow.close();
      alert(error.message || "Unable to print the report.");
    }
  };

  // Renders the formal report to canvas and splits it into A4 PDF pages.
  // Returns the PDF blob only â€” does NOT trigger a browser download, so it
  // can be reused for email attachments and WhatsApp file sharing without
  // opening a second save dialog. Use handleDownloadPdfClick for the
  // toolbar "Download PDF" button, which wraps this and saves the file.
  const downloadPdf = async () => {
    if (!validate()) return;

    let container = null;

    try {
      setGeneratingPdf(true);

      const exportObservations = await prepareExportObservations();

      container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-12000px";
      container.style.top = "0";
      container.style.width = "794px";
      container.innerHTML = `
        <style>${EXPORT_REPORT_CSS}</style>
        ${buildReportExportHtml(exportObservations)}
      `;

      document.body.appendChild(container);

      const reportDocument = container.querySelector(".cr-export-document");

      if (!reportDocument) {
        throw new Error("The formal report document was not generated.");
      }
      /*
  Pehle images load honi chahiye, tabhi photo grid ki
  correct height calculate hogi.
*/
      await waitForImages(reportDocument);

      applyPdfPageBreaks(reportDocument);

      /*
  Spacer add hone ke baad browser ko layout refresh ka time.
*/
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)),
      );

      const fullCanvas = await html2canvas(reportDocument, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        imageTimeout: 20000,
        scrollX: 0,
        scrollY: 0,
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pageWidthPx = fullCanvas.width;
      const a4PageHeightPx = Math.round((pageWidthPx * 297) / 210);

      const footerHeightPx = Math.round((pageWidthPx * 16) / 210);

      const topMarginPx = Math.round((pageWidthPx * 8) / 210);

      const getPageTopMargin = (currentPageIndex) =>
        currentPageIndex === 0 ? 0 : topMarginPx;

      const contentHeightPx = a4PageHeightPx - footerHeightPx;
      let sourceY = 0;
      let pageIndex = 0;

      while (sourceY < fullCanvas.height) {
        const pageTopMargin = getPageTopMargin(pageIndex);

        const pageContentHeight = contentHeightPx - pageTopMargin;

        const availableSourceHeight = Math.min(
          pageContentHeight,
          fullCanvas.height - sourceY,
        );

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = pageWidthPx;
        pageCanvas.height = a4PageHeightPx;

        const context = pageCanvas.getContext("2d");

        if (!context) {
          throw new Error("Unable to create an A4 PDF page.");
        }

        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

        // Continuous report content: next page starts exactly where
        // previous A4 page content ended.
        context.drawImage(
          fullCanvas,
          0,
          sourceY,
          pageWidthPx,
          availableSourceHeight,

          // Destination position
          0,
          pageTopMargin,

          pageWidthPx,
          availableSourceHeight,
        );

        // Footer on EVERY A4 page.
        const footerY = a4PageHeightPx - footerHeightPx;

        context.fillStyle = "#14234f";
        context.fillRect(0, footerY, pageWidthPx, footerHeightPx);

        const footerScale = pageWidthPx / 794;

        context.textBaseline = "alphabetic";

        context.fillStyle = "#ffffff";
        context.font = `700 ${Math.round(9 * footerScale)}px Arial`;

        context.fillText(
          "Sheth Design Consultants LLP",
          Math.round(34 * footerScale),
          footerY + Math.round(23 * footerScale),
        );

        context.fillStyle = "#cbd5e1";
        context.font = `${Math.round(7 * footerScale)}px Arial`;

        context.fillText(
          "Construction Site Visit Report",
          Math.round(34 * footerScale),
          footerY + Math.round(38 * footerScale),
        );

        const pageNumberText = `Page ${pageIndex + 1}`;
        const dateText = formatReportDate(savedReport?.createdAt || new Date());

        context.textAlign = "right";

        context.fillStyle = "#ffffff";
        context.font = `700 ${Math.round(8 * footerScale)}px Arial`;

        context.fillText(
          pageNumberText,
          pageWidthPx - Math.round(34 * footerScale),
          footerY + Math.round(23 * footerScale),
        );

        context.fillStyle = "#cbd5e1";
        context.font = `${Math.round(7 * footerScale)}px Arial`;

        context.fillText(
          dateText,
          pageWidthPx - Math.round(34 * footerScale),
          footerY + Math.round(38 * footerScale),
        );

        context.textAlign = "left";

        const pageImage = pageCanvas.toDataURL("image/jpeg", 0.96);

        if (pageIndex > 0) {
          pdf.addPage("a4", "portrait");
        }

        pdf.addImage(pageImage, "JPEG", 0, 0, 210, 297, undefined, "FAST");

        sourceY += availableSourceHeight;
        pageIndex += 1;
      }

      const pdfBlob = pdf.output("blob");
      return pdfBlob;
    } catch (error) {
      console.error("PDF generation failed:", error);

      alert(
        `Unable to generate PDF: ${
          error?.message || "Unknown error. Check the browser console."
        }`,
      );
    } finally {
      if (container?.parentNode) {
        container.parentNode.removeChild(container);
      }

      setGeneratingPdf(false);
    }
  };

  // Used by the toolbar "Download PDF" button â€” generates the PDF via
  // downloadPdf() and then actually saves it to the user's device.
  // downloadPdf() itself only returns the blob so email/WhatsApp can reuse
  // it without triggering a second file-save dialog.
  const handleDownloadPdfClick = async () => {
    const pdfBlob = await downloadPdf();
    if (!pdfBlob) return;

    const safeProjectName = String(form.projectName || "Construction_Report")
      .replace(/[^a-zA-Z0-9-_ ]/g, "")
      .trim()
      .replace(/\s+/g, "_");

    const visitDate = form.visitDateTime
      ? new Date(form.visitDateTime).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);

    triggerBlobDownload(pdfBlob, `${safeProjectName}_${visitDate}.pdf`);
  };

  if (loadingReport) {
    return (
      <div className="cr-report-loading">
        <div>
          <h3>Loading Report</h3>
          <p>
            Please wait while the saved construction report is being loaded.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="construction-report-page">
      {/* =====================================================
          PAGE TOOLBAR
          Report title on left and all report actions in one row.
      ===================================================== */}
      <div className="cr-toolbar no-print">
        <div className="cr-toolbar-title">
          <p className="cr-eyebrow">Operations / Architect</p>
          <h1>Construction Site Visit Report</h1>
          <p className="cr-subtitle">
            Create and share a complete construction site visit report.
          </p>

          {reportId && (
            <div className="cr-view-report-badge">Viewing Saved Report</div>
          )}
        </div>

        <div className="cr-toolbar-right">
          <button
            type="button"
            className="cr-btn cr-btn-light cr-saved-report-btn"
            onClick={() => navigate("/construction-reports/saved")}
          >
            See All Saved Reports
          </button>

          <div className="cr-toolbar-actions">
            <button
              type="button"
              className="cr-btn cr-btn-light"
              onClick={printReport}
            >
              Print Report
            </button>

            <button
              type="button"
              className="cr-btn cr-btn-pdf"
              disabled={generatingPdf}
              onClick={handleDownloadPdfClick}
            >
              {generatingPdf ? "Generating PDF..." : "Download PDF"}
            </button>

            <button
              type="button"
              className="cr-btn cr-btn-email"
              disabled={saving || sendingEmail}
              onClick={sendEmail}
            >
              {sendingEmail ? "Sending..." : "Send Email"}
            </button>

            <button
              type="button"
              className="cr-btn cr-btn-whatsapp"
              disabled={saving}
              onClick={sendWhatsApp}
            >
              Send WhatsApp
            </button>
          </div>
        </div>
      </div>

      <div className="cr-report-sheet">
        <section className="cr-section">
          <div className="cr-section-title">
            <span>01</span>
            <div>
              <h3>Project & Visit Information</h3>
              <p>Select project, visit date/time, members and site location.</p>
            </div>
          </div>

          <div className="cr-form-grid cr-project-info-grid">
            <label className="cr-field cr-project-field">
              <span>Project Name *</span>
              <Select
                options={projects.map((project) => ({
                  value: project._id,
                  label: project.projectName || project.name,
                }))}
                value={
                  projects
                    .map((project) => ({
                      value: project._id,
                      label: project.projectName || project.name,
                    }))
                    .find((option) => option.value === form.project) || null
                }
                onChange={(option) => onProjectChange(option?.value || "")}
                placeholder="Select or Search Project..."
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "46px",
                    height: "45px",
                    borderRadius: "12px",
                  }),

                  valueContainer: (base) => ({
                    ...base,
                    height: "56px",
                    padding: "0 14px",
                  }),

                  input: (base) => ({
                    ...base,
                    margin: 0,
                    padding: 0,
                  }),

                  indicatorsContainer: (base) => ({
                    ...base,
                    height: "56px",
                  }),

                  dropdownIndicator: (base) => ({
                    ...base,
                    padding: "0 12px",
                  }),

                  indicatorSeparator: (base) => ({
                    ...base,
                    height: "32px",
                    marginTop: "12px",
                    marginBottom: "12px",
                  }),

                  placeholder: (base) => ({
                    ...base,
                    fontSize: "16px",
                  }),

                  singleValue: (base) => ({
                    ...base,
                    fontSize: "16px",
                  }),

                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </label>

            <label className="cr-field cr-visit-field">
              <span>Visit Date & Time *</span>
              <input
                type="datetime-local"
                value={form.visitDateTime}
                onChange={(e) => {
                  updateForm("visitDateTime", e.target.value);
                  setSavedReport(null);
                }}
              />
            </label>
            <label className="cr-field cr-prepared-field">
              <span>Prepared By *</span>
              <select
                value={form.preparedBy}
                onChange={(e) => {
                  updateForm("preparedBy", e.target.value);
                  setSavedReport(null);
                }}
              >
                <option value="">Select user</option>
                {users.map((preparedUser) => (
                  <option key={preparedUser._id} value={preparedUser._id}>
                    {getUserName(preparedUser)}
                  </option>
                ))}
              </select>
            </label>

            <label className="cr-field cr-received-field">
              <span>Received By</span>
              <input
                type="text"
                list="receivedByUsers"
                value={form.reviewedBy}
                onChange={(e) => {
                  updateForm("reviewedBy", e.target.value);
                  setSavedReport(null);
                }}
                placeholder="Type or select user"
              />
              <datalist id="receivedByUsers">
                {users.map((receivedUser) => (
                  <option
                    key={receivedUser._id}
                    value={getUserName(receivedUser)}
                  />
                ))}
              </datalist>
            </label>
            <div className="cr-site-details-row">
              <div className="cr-field cr-location-field">
                <span>Site Location *</span>
                <div className="cr-location-control">
                  {/* Read-only textarea is used so a long address can wrap. */}
                  <textarea
                    readOnly
                    rows={2}
                    value={form.siteLocation?.address || ""}
                    placeholder="Select site location from map"
                    className="cr-location-address"
                  />
                  <button
                    type="button"
                    className="cr-btn cr-btn-secondary no-print"
                    onClick={() => setMapOpen(true)}
                  >
                    Select Location
                  </button>
                </div>
                {form.siteLocation?.lat && form.siteLocation?.lng && (
                  <small>
                    Coordinates: {form.siteLocation.lat.toFixed(6)},{" "}
                    {form.siteLocation.lng.toFixed(6)}
                  </small>
                )}
              </div>
              <label className="cr-field cr-contractor-field">
                <span>Contractor</span>
                <input
                  value={form.contractor}
                  onChange={(e) => {
                    updateForm("contractor", e.target.value);
                    setSavedReport(null);
                  }}
                  placeholder="Contractor name"
                />
              </label>

              <label className="cr-field cr-client-field">
                <span>Client Representative</span>
                <input
                  value={form.clientRepresentative}
                  onChange={(e) => {
                    updateForm("clientRepresentative", e.target.value);
                    setSavedReport(null);
                  }}
                  placeholder="Representative name"
                />
              </label>
            </div>
          </div>
        </section>

        <section className="cr-section">
          <div className="cr-section-title cr-section-title-row">
            <div className="cr-title-left">
              <span>02</span>
              <div>
                <h3>Site Observations</h3>
                <p>Attach up to 4 photos to each observation.</p>
              </div>
            </div>
            <div className="cr-summary-chip">
              {completedCount}/{observations.length} completed
            </div>
          </div>

          <div className="cr-observation-list">
            {observations.map((observation, index) => (
              <article
                className="cr-observation-card"
                key={observation.clientId}
              >
                <div className="cr-observation-top">
                  <div className="cr-observation-number">
                    Observation {String(index + 1).padStart(2, "0")}
                  </div>
                  <button
                    type="button"
                    className="cr-icon-btn no-print"
                    disabled={observations.length === 1}
                    onClick={() => removeObservation(observation.clientId)}
                  >
                    Ã—
                  </button>
                </div>

                <div className="cr-observation-grid">
                  <label className="cr-field">
                    <span>Subject</span>
                    <select
                      value={observation.subject}
                      onChange={(e) => {
                        updateObservation(
                          observation.clientId,
                          "subject",
                          e.target.value,
                        );
                        setSavedReport(null);
                      }}
                    >
                      <option value="">Select subject</option>
                      {SUBJECT_OPTIONS.map((subject) => (
                        <option key={subject} value={subject}>
                          {subject}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="cr-field">
                    <span>Status</span>
                    <select
                      value={observation.status}
                      onChange={(e) => {
                        updateObservation(
                          observation.clientId,
                          "status",
                          e.target.value,
                        );
                        setSavedReport(null);
                      }}
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="cr-field">
                    <span>Target Date</span>
                    <input
                      type="date"
                      value={observation.dueDate}
                      onChange={(e) => {
                        updateObservation(
                          observation.clientId,
                          "dueDate",
                          e.target.value,
                        );
                        setSavedReport(null);
                      }}
                    />
                  </label>

                  <div className="cr-observation-split">
                    <div className="cr-field cr-observation-description-panel">
                      <div className="cr-description-head">
                        <span>
                          Description / Site Observation With Solution *
                        </span>
                      </div>

                      {/*
                        Bullet description editor:
                        - Starts as one compact line.
                        - Enter creates the next bullet automatically.
                        - Height grows with the number of points.
                      */}
                      <textarea
                        className="cr-observation-description-textarea"
                        rows={1}
                        value={observation.description}
                        /* =====================================================
     DESCRIPTION FOCUS
     Empty description me first bullet add karta hai.
     Textarea DOM reference callback se pehle save karta hai.
  ===================================================== */
                        onFocus={(e) => {
                          const textarea = e.currentTarget;

                          if (!observation.description.trim()) {
                            updateObservation(
                              observation.clientId,
                              "description",
                              "â€¢ ",
                            );

                            setSavedReport(null);
                          }

                          requestAnimationFrame(() => {
                            if (!textarea) return;

                            textarea.style.height = "44px";

                            textarea.style.height = `${Math.max(
                              44,
                              textarea.scrollHeight,
                            )}px`;
                          });
                        }}
                        /* =====================================================
     DESCRIPTION CHANGE
     User typing ko state me save karta hai.
     Content ke according textarea height grow karta hai.
  ===================================================== */
                        onChange={(e) => {
                          const textarea = e.currentTarget;
                          const value = textarea.value;

                          updateObservation(
                            observation.clientId,
                            "description",
                            value,
                          );

                          setSavedReport(null);

                          textarea.style.height = "44px";

                          textarea.style.height = `${Math.max(
                            44,
                            textarea.scrollHeight,
                          )}px`;
                        }}
                        /* =====================================================
     AUTO BULLET ON ENTER
     Enter press par next bullet create karta hai.
     Empty bullet ke baad extra empty bullet create nahi karta.
  ===================================================== */
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;

                          e.preventDefault();

                          const textarea = e.currentTarget;

                          if (!textarea) return;

                          const value = observation.description || "";

                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;

                          const lineStart =
                            value.lastIndexOf("\n", start - 1) + 1;

                          const currentLine = value
                            .slice(lineStart, start)
                            .replace(/^â€¢\s*/, "")
                            .trim();

                          // Current bullet empty hai to another empty bullet mat banao.
                          if (!currentLine) {
                            return;
                          }

                          const newValue =
                            value.slice(0, start) + "\nâ€¢ " + value.slice(end);

                          updateObservation(
                            observation.clientId,
                            "description",
                            newValue,
                          );

                          setSavedReport(null);

                          requestAnimationFrame(() => {
                            if (!textarea) return;

                            const nextPosition = start + 3;

                            textarea.selectionStart = nextPosition;
                            textarea.selectionEnd = nextPosition;

                            textarea.style.height = "44px";

                            textarea.style.height = `${Math.max(
                              44,
                              textarea.scrollHeight,
                            )}px`;
                          });
                        }}
                        placeholder="â€¢ Enter site observation"
                      />
                    </div>

                    {/* =================================================
                        OBSERVATION PHOTO MANAGER
                        Shows selected photos, Add Photo and compact count.
                    ================================================= */}
                    <div className="cr-field cr-observation-photo-panel">
                      <div className="cr-inline-photos">
                        {observation.photos.map((photo, photoIndex) => (
                          <div
                            className="cr-photo-tile"
                            key={`${photo.name}-${photoIndex}`}
                          >
                            <button
                              type="button"
                              className="cr-photo-preview-btn"
                              onClick={() =>
                                setPreviewImage({
                                  src: photo.preview,
                                  name: photo.name,
                                })
                              }
                            >
                              <img
                                src={photo.preview}
                                alt="Observation attachment"
                              />
                            </button>

                            <button
                              type="button"
                              className="cr-photo-remove no-print"
                              onClick={() =>
                                removePhoto(observation.clientId, photoIndex)
                              }
                            >
                              Ã—
                            </button>
                          </div>
                        ))}

                        {observation.photos.length < 4 && (
                          <label className="cr-photo-add no-print">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => {
                                addPhotos(observation.clientId, e.target.files);
                                e.target.value = "";
                              }}
                            />

                            <span>+</span>
                            <small>Add Photo</small>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <button
            type="button"
            className="cr-add-observation no-print"
            onClick={() => {
              setObservations((old) => [...old, makeObservation()]);
              setSavedReport(null);
            }}
          >
            + Add Observation
          </button>
        </section>

        <section className="cr-section">
          <div className="cr-section-title">
            <span>03</span>
            <div>
              <h3>Conclusion & Sign-off</h3>
            </div>
          </div>

          <div className="cr-form-grid">
            <label className="cr-field cr-span-4">
              <span>Overall Site Visit Remark</span>
              <textarea
                rows="3"
                value={form.overallRemark}
                onChange={(e) => {
                  updateForm("overallRemark", e.target.value);
                  setSavedReport(null);
                }}
              />
            </label>

            <div className="cr-field cr-signature-field">
              <span>Authorized Signature</span>
              <div className="cr-print-signature">
                <div className="cr-sign-space" />
                <div className="cr-sign-line" />
                <p>(Sign & Seal)</p>
              </div>
            </div>
          </div>
        </section>

        <div
          className="cr-bottom-save no-print"
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "30px 0 40px",
          }}
        >
          <button
            type="button"
            className="cr-btn cr-btn-primary cr-final-save-btn"
            disabled={saving}
            onClick={saveReport}
          >
            {saving ? "Saving Report..." : "Save Report"}
          </button>
        </div>
      </div>

      {previewImage &&
        createPortal(
          <div
            className="cr-image-preview-overlay"
            onMouseDown={() => setPreviewImage(null)}
          >
            <div
              className="cr-image-preview-modal"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="cr-image-preview-header">
                <strong>{previewImage.name || "Observation Photo"}</strong>

                <button type="button" onClick={() => setPreviewImage(null)}>
                  Ã—
                </button>
              </div>

              <div className="cr-image-preview-body">
                <img
                  src={previewImage.src}
                  alt={previewImage.name || "Observation preview"}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
      <MapPicker
        open={mapOpen}
        value={form.siteLocation}
        onClose={() => setMapOpen(false)}
        onSelect={(location) => {
          updateForm("siteLocation", location);
          setSavedReport(null);
          setMapOpen(false);
        }}
      />
      {emailModalOpen &&
        createPortal(
          <div
            onMouseDown={() => {
              if (!sendingEmail) setEmailModalOpen(false);
            }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(15, 23, 42, 0.45)",
              padding: "16px",
            }}
          >
            <div
              onMouseDown={(event) => event.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: "440px",
                background: "#ffffff",
                borderRadius: "10px",
                boxShadow: "0 20px 60px rgba(15, 23, 42, 0.35)",
                padding: "22px 24px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", color: "#172033" }}>
                Select Recipients
              </h3>

              <p
                style={{
                  fontSize: "12px",
                  color: "#667085",
                  margin: "6px 0 14px",
                }}
              >
                Hold Ctrl (Windows) or Cmd (Mac) and click to select multiple
                email addresses.
              </p>

              {availableEmails.length === 0 ? (
                <p style={{ fontSize: "13px", color: "#667085" }}>
                  No client email is saved on this project yet. Add one below.
                </p>
              ) : (
                <select
                  multiple
                  value={selectedEmails}
                  onChange={(e) => {
                    const chosen = Array.from(e.target.selectedOptions).map(
                      (option) => option.value,
                    );
                    setSelectedEmails(chosen);
                  }}
                  style={{
                    width: "100%",
                    minHeight: "120px",
                    padding: "8px",
                    borderRadius: "6px",
                    border: "1px solid #dbe3ee",
                    fontSize: "13px",
                    boxSizing: "border-box",
                  }}
                >
                  {availableEmails.map((email) => (
                    <option key={email} value={email}>
                      {email}
                    </option>
                  ))}
                </select>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  marginTop: "12px",
                  marginBottom: "4px",
                }}
              >
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addNewEmailToList();
                    }
                  }}
                  placeholder="Add another email address"
                  style={{
                    flex: 1,
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: "1px solid #dbe3ee",
                    boxSizing: "border-box",
                  }}
                />
                <button
                  type="button"
                  className="cr-btn cr-btn-secondary"
                  onClick={addNewEmailToList}
                >
                  Add
                </button>
              </div>

              {selectedEmails.length > 0 && (
                <p
                  style={{
                    fontSize: "12px",
                    color: "#354258",
                    marginTop: "6px",
                  }}
                >
                  Selected ({selectedEmails.length}):{" "}
                  {selectedEmails.join(", ")}
                </p>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                  marginTop: "18px",
                }}
              >
                <button
                  type="button"
                  className="cr-btn cr-btn-light"
                  onClick={() => setEmailModalOpen(false)}
                  disabled={sendingEmail}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="cr-btn cr-btn-primary"
                  onClick={confirmSendEmail}
                  disabled={sendingEmail}
                >
                  {sendingEmail ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

